import { useState, useEffect, useRef } from 'react';

interface UseDelayedLoadingOptions {
  /**
   * The loading state to monitor
   */
  loading: boolean;
  /**
   * Minimum delay in milliseconds before showing skeleton (default: 300ms)
   * If loading completes before this delay, skeleton never shows
   */
  delay?: number;
}

/**
 * Hook that delays showing loading state to prevent flickering on fast loads.
 * 
 * Strategy:
 * - If load < delay (default 300ms) → show nothing (instant content)
 * - If load >= delay → show skeleton
 * 
 * @example
 * const { showSkeleton } = useDelayedLoading({ loading: isLoading, delay: 300 });
 * <TableSkeletonLoader loading={showSkeleton} ...>
 */
export function useDelayedLoading({
  loading,
  delay = 300,
}: UseDelayedLoadingOptions): { showSkeleton: boolean } {
  const [showSkeleton, setShowSkeleton] = useState(false);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const loadingStartedRef = useRef<number | null>(null);

  useEffect(() => {
    if (loading) {
      // Loading started - record the start time
      loadingStartedRef.current = Date.now();
      
      // Set a timeout to show skeleton after delay
      timeoutRef.current = setTimeout(() => {
        setShowSkeleton(true);
      }, delay);
    } else {
      // Loading finished - clear timeout and hide skeleton
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      
      // If loading was very fast (< delay), never show skeleton
      // Otherwise, hide skeleton immediately when loading finishes
      setShowSkeleton(false);
      loadingStartedRef.current = null;
    }

    // Cleanup on unmount or when loading changes
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
  }, [loading, delay]);

  return { showSkeleton };
}
